import {
  type ReactNode,
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { type OidcConfig, type SessionInfo, getOidcConfig, getSession, logout } from './api';

/**
 * Sesja i konfiguracja logowania — jedno źródło prawdy dla całej aplikacji.
 *
 * Kontekst, a nie hook pobierający dane u każdego konsumenta: nagłówek, ekran
 * główny i odtwarzacz potrzebują tej samej informacji jednocześnie, więc bez
 * współdzielenia każdy z nich wysyłałby własną parę żądań i mogłyby się
 * rozejść — nagłówek pokazywałby konto, a półka „kontynuuj" jeszcze nie.
 *
 * Oba zapytania idą razem, bo interfejs potrzebuje ich jednocześnie: bez
 * `config.enabled` nie wiadomo, czy pokazywać przycisk logowania, a bez
 * `session` — czy zamiast niego pokazać nazwę konta.
 */

export interface SessionState {
  loading: boolean;
  config: OidcConfig | null;
  session: SessionInfo | null;
  /** Odświeżenie po powrocie z Authority albo po wylogowaniu. */
  refresh: () => void;
  signOut: () => Promise<void>;
}

const ANONYMOUS: SessionInfo = { authenticated: false, user: null };

const SessionContext = createContext<SessionState>({
  loading: true,
  config: null,
  session: null,
  refresh: () => undefined,
  signOut: async () => undefined,
});

export function SessionProvider({ children }: { children: ReactNode }) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<OidcConfig | null>(null);
  const [session, setSession] = useState<SessionInfo | null>(null);

  // Odmontowanie sprawdzamy refem, a nie licznikiem odświeżeń w zależnościach
  // efektu: ten sam kod pobierania obsługuje pierwsze wczytanie i każde
  // późniejsze odświeżenie, więc trzymamy go w jednej funkcji.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    setLoading(true);
    // allSettled, nie all: awaria jednego zapytania nie może zostawić
    // interfejsu bez informacji z drugiego.
    void Promise.allSettled([getOidcConfig(), getSession()]).then(([cfg, ses]) => {
      if (!mountedRef.current) return;
      setConfig(cfg.status === 'fulfilled' ? cfg.value : null);
      setSession(ses.status === 'fulfilled' ? ses.value : ANONYMOUS);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    const result = await logout().catch(() => null);
    setSession(ANONYMOUS);
    // Wylogowanie u dostawcy jest opcjonalne: sesja aplikacji jest już
    // unieważniona, więc gdy Authority nie wystawia end_session, po prostu
    // zostajemy na miejscu zamiast pokazywać błąd.
    if (result?.providerLogoutUrl != null) {
      window.location.assign(result.providerLogoutUrl);
      return;
    }
    refresh();
  }, [refresh]);

  const value = useMemo<SessionState>(
    () => ({ loading, config, session, refresh, signOut }),
    [loading, config, session, refresh, signOut],
  );

  // createElement, nie JSX: ten plik jest .ts, a przeniesienie go do .tsx
  // zmieniłoby ścieżkę importu w kilku miejscach bez żadnego zysku.
  return createElement(SessionContext.Provider, { value }, children);
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}
